import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createProductAdmin,
  deleteProductAdmin,
  getProductAdmin,
  listCostModels,
  listProductCategories,
  listProductSubcategories,
  listProductsAdmin,
  updateProductAdmin,
  type CategoryOption,
  type CostModelOption,
  type CreateProductAdminBody,
  type ListProductsAdminParams,
  type PaginatedProductsAdmin,
  type ProductAdmin,
  type SubcategoryOption,
  type UpdateProductAdminBody,
} from "../api/productsAdmin";

/** Paginated products list (§L — HTTP via TanStack Query). */
export function useProductsAdmin(params: ListProductsAdminParams) {
  return useQuery<PaginatedProductsAdmin>({
    queryKey: ["admin-products", "list", params],
    queryFn: () => listProductsAdmin(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * Infinite products list for dropdowns — paginates `/v1/admin/products` by
 * `page`, accumulating pages so the menu can load more on scroll-to-bottom.
 */
export function useInfiniteProductsAdmin(
  params: Omit<ListProductsAdminParams, "page"> = {},
) {
  const limit = params.limit ?? 10;
  return useInfiniteQuery<PaginatedProductsAdmin>({
    queryKey: ["admin-products", "infinite", { ...params, limit }],
    queryFn: ({ pageParam }) =>
      listProductsAdmin({ ...params, limit, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.totalPages
        ? last.pagination.page + 1
        : undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Single product detail for View / Edit prefill. */
export function useProductAdmin(productId: string | null) {
  return useQuery<ProductAdmin>({
    queryKey: ["admin-products", "detail", productId],
    queryFn: () => getProductAdmin(productId as string),
    enabled: !!productId,
    staleTime: 30_000,
  });
}

export function useCreateProductAdmin() {
  const queryClient = useQueryClient();
  return useMutation<ProductAdmin, Error, CreateProductAdminBody>({
    mutationFn: (body) => createProductAdmin(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-products"] });
    },
  });
}

export function useUpdateProductAdmin() {
  const queryClient = useQueryClient();
  return useMutation<
    ProductAdmin,
    Error,
    { productId: string; body: UpdateProductAdminBody }
  >({
    mutationFn: ({ productId, body }) => updateProductAdmin(productId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-products"] });
    },
  });
}

/** Soft-delete via DELETE /v1/admin/products/:id. */
export function useDeleteProductAdmin() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, string>({
    mutationFn: (productId) => deleteProductAdmin(productId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-products"] });
    },
  });
}

/** Categories dropdown — DISTINCT values across products. */
export function useProductCategories() {
  return useQuery<CategoryOption[]>({
    queryKey: ["admin-products", "categories"],
    queryFn: () => listProductCategories(),
    staleTime: 5 * 60_000,
  });
}

/** Subcategories dropdown — optionally filtered by parent category. */
export function useProductSubcategories(category?: string) {
  return useQuery<SubcategoryOption[]>({
    queryKey: ["admin-products", "subcategories", category ?? "*"],
    queryFn: () => listProductSubcategories(category),
    staleTime: 5 * 60_000,
  });
}

/** Cost-model dropdown — read from cost_models. */
export function useCostModels() {
  return useQuery<CostModelOption[]>({
    queryKey: ["admin-products", "cost-models"],
    queryFn: () => listCostModels(),
    staleTime: 5 * 60_000,
  });
}
